import { Injectable, Injector } from '@angular/core';
import { ListData } from '@fi-app/components/items-list/list-data.service';
import { DocumentModel } from './document.model';
import { ObjectTypes } from '@fi-shared/types/object-type';
import {
    ItemsListElementType,
    ItemsListHeader,
    ItemsListHeaderProperties,
} from '../components/items-list/items-list-header';
import { DocumentService } from './document.service';
import { AppTextService } from '@fi-shared/app-text';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { FiNotificationHelperService } from '@fi-shared/fi-notification/fi-notification-helper.service';
import { IDocumentModel } from '@fi-app/document/document';
import { FiSiteDesignAccessItemCellComponent } from '@fi-app/fi-site-design/fi-site-design-access-item-cell/fi-site-design-access-item-cell.component';
import { takeUntil } from 'rxjs/operators';
import { FiItemsListType } from '@fi-shared/fi-items-list/fi-items-list-type';
import { FiItemsDeleteHelperService } from '@fi-shared/fi-shared-services/fi-items-delete-helper.service';

interface IDocumentListOptions {
    isFavoriteIconShown?: boolean;
    isLocalSort?: boolean;
}

@Injectable()
export class DocumentListDataService {
    constructor(
        private injector: Injector,
        private documentService: DocumentService,
    ) {}

    getInstance(opts?: IDocumentListOptions) {
        return new DocumentListData(this.injector, this.documentService, opts);
    }
}

export class DocumentListData extends ListData<DocumentModel> {
    constructor(injector: Injector, documentService: DocumentService, opts: IDocumentListOptions) {
        if (!opts) {
            opts = {};
        }

        super(injector, opts.isLocalSort, 'PublicationStartDate', true);

        const itemsDeleteHelperService = injector.get(FiItemsDeleteHelperService);

        this.ShowFavoriteIcon = opts.isFavoriteIconShown;
        this.isV2SearchByContent = true;

        this.ObjectType = ObjectTypes.Document;

        this.DeletePermanentlyTextId = 'document.delete_permanently_confirm';

        this.Header.push(
            new ItemsListHeader(
                '',
                '',
                new ItemsListHeaderProperties(
                    'CcmListItem-Icon CcmListItem-NoBorder',
                    true,
                    ItemsListElementType.ImgIconA,
                    {
                        Sref: () => FiStateNames.Document.View,
                        SrefParams: (data) => ({ Id: data.Id }),
                        ImgSrc: 'Image',
                        ImgSrcLarge: 'ImageLarge',
                    },
                ),
            ),
        );

        this.Header.push(
            new ItemsListHeader(
                this.appTextService.get('common.name'),
                'Name',
                new ItemsListHeaderProperties('CcmListItem-Name', true, ItemsListElementType.A, {
                    Sref: () => FiStateNames.Document.View,
                    SrefParams: (data) => ({ Id: data.Id }),
                    secondaryValue: (data) => {
                        return (
                            this.fiFormatDateService.formatDate(data.ChangedOn, 'date') + ' ' + data.ChangedByUserName
                        );
                    },
                }),
            ),
        );

        if (this.userDataService.showSiteDesignAccessForItems()) {
            this.Header.push(
                new ItemsListHeader(
                    this.appTextService.get('site_design.brands'),
                    'SiteDesignIds',
                    new ItemsListHeaderProperties('CcmListItem-Brand', false, FiSiteDesignAccessItemCellComponent),
                ),
            );
        }

        this.Header.push(
            new ItemsListHeader(
                this.appTextService.get('common.tags'),
                'Tags',
                new ItemsListHeaderProperties('', false, ItemsListElementType.Tags),
                'Name',
            ),
        );

        this.addStartDateHeader(true);

        this.addChangedOnHeader();

        this.Header.push(
            new ItemsListHeader(
                this.appTextService.get('common.remark'),
                'IsPublished',
                new ItemsListHeaderProperties('', true, ItemsListElementType.SpanPlain, {
                    getMainValueClass: (data: DocumentModel) => {
                        if (!data) {
                            return '';
                        }
                        if (data.IsDeleted) {
                            return 'CcmListItemRed';
                        }
                        if (data.IsPublished) {
                            return 'CcmListItemGreen';
                        }
                        return 'CcmListItemOrange';
                    },
                }),
                undefined,
                undefined,
                (data: DocumentModel) => {
                    if (data.IsDeleted) return this.appTextService.get('document.deleted');
                    if (data.IsPublished) return '';
                    return this.appTextService.get('common.not_shared');
                },
            ),
        );

        this.deleteConfirm = (deleteConfirmMessage: string, selectedIds: number[], selectedItems: DocumentModel[]) => {
            return itemsDeleteHelperService.documentDeleteConfirm(
                selectedItems,
                this.appTextService.get(this.DeletePermanentlyTextId),
            );
        };

        this.deleteFunc = (selectedIds) => documentService.deleteDocuments(selectedIds);

        this.restoreFunc = (documentsToRestore) => {
            const documentIdsToRestore = documentsToRestore.map((document) => document.Id);
            return documentService.RestoreDocuments(documentIdsToRestore);
        };

        const notificationHelper = this.injector.get(FiNotificationHelperService);
        this.getRowClass = (document: DocumentModel) => {
            const result: string[] = [];

            if (notificationHelper.isNotSeen(document)) result.push('not-seen');
            if (notificationHelper.isNotVisited(document)) result.push('bold');

            return result.join(' ');
        };

        documentService.previewImageGenerated$.pipe(takeUntil(this.ngOnDestroy$)).subscribe((documentId) => {
            const currentItem = this.Data.find((item) => item.Id === documentId);
            if (currentItem) {
                currentItem.ChangedOn = new Date();
                super.initializeImage(currentItem, currentItem.PreviewFileName);
                this.Header[0].Properties.updateContent$.next();
            }
        });

        this.searchV2Changed$.pipe(takeUntil(this.ngOnDestroy$)).subscribe((localSearchParameter) => {
            this.setHighlightTextParam(localSearchParameter.ContentSearchTexts);
        });
    }

    InitializeData(items: IDocumentModel[]) {
        const documents = [];
        items.forEach((item) => {
            const document = new DocumentModel(undefined, undefined, this.injector);
            super.initializeImage(document, item.PreviewFileName);
            document.Initialize(item, true, this.injector);
            documents.push(document);
        });
        this.appendData(documents);
    }

    protected getKeyForListSort(): FiItemsListType {
        return FiItemsListType.Documents;
    }
}
